import { useState } from 'react';

import defaultRequestErrorHandler from 'src/api/notifications/defaultRequestErrorHandler';
import { LoginByPasswordErrorKey } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useLogin/types';
import { postLoginByPassword } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useLogin/utils';
import { useNotification } from 'src/components/Notifications/Provider';

import {
    TwoFactorEventDataResponseKey,
    type PostTwoFactorEventPayload,
    type PostTwoFactorEventResendPayload,
    type ResendTwoFactorCodeParams,
    type ResendTwoFactorCodeReturn,
    type SendTwoFactorCodeParams,
    type SendTwoFactorCodeReturn,
} from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useTwoFactor/types';
import { postTwoFactorEvent } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useTwoFactor/utils';

interface UseTwoFactorHook {
    isLoading: boolean;
    sendTwoFactorCode: (params: SendTwoFactorCodeParams) => Promise<SendTwoFactorCodeReturn>;
    resendTwoFactorCode: (params: ResendTwoFactorCodeParams) => Promise<ResendTwoFactorCodeReturn>;
}

const useTwoFactor = (): UseTwoFactorHook => {
    const [isLoading, setIsLoading] = useState(false);

    const { addNotification } = useNotification();

    const sendTwoFactorCode: UseTwoFactorHook['sendTwoFactorCode'] = async ({ username, event }) => {
        setIsLoading(true);

        try {
            const payload: PostTwoFactorEventPayload = { username, event };

            const result = await postTwoFactorEvent(payload);
            setIsLoading(false);

            if (result.success) {
                return { success: true, data: result.data };
            }

            return { success: false, error: TwoFactorEventDataResponseKey.Unknown };
        } catch (error) {
            setIsLoading(false);
            defaultRequestErrorHandler(error, addNotification);

            return { success: false, error: TwoFactorEventDataResponseKey.Unknown };
        }
    };

    const resendTwoFactorCode: UseTwoFactorHook['resendTwoFactorCode'] = async (params) => {
        setIsLoading(true);

        try {
            const payload: PostTwoFactorEventResendPayload = { ...params };
            payload.resend_code_2fa = true;

            await sendTwoFactorCode({ username: payload.username, event: 'resend' });

            const result = await postLoginByPassword(payload);
            setIsLoading(false);

            if (result.success) {
                return { success: true, data: result.data };
            }

            return { success: false, error: result.error };
        } catch (error) {
            setIsLoading(false);
            defaultRequestErrorHandler(error, addNotification);

            return { success: false, error: { key: LoginByPasswordErrorKey.Unknown } };
        }
    };

    return { isLoading, sendTwoFactorCode, resendTwoFactorCode };
};

export default useTwoFactor;
